import { gsap } from 'gsap';
import { SplitText } from 'gsap/SplitText';

import { ElementsManager } from './ElementsManager';
gsap.registerPlugin(SplitText);

export class ChatBox {
  private elementsManager: ElementsManager;
  private conversationHistory: any[] = []; // Store conversation history

  constructor() {
    this.elementsManager = new ElementsManager();
    const chatGPTpromptText = this.elementsManager.getInputElement('chatGPTprompt')
      ?.textContent as string;
    const memberNameText = this.elementsManager.getInputElement('memberName')
      ?.textContent as string;
    const replacedText = chatGPTpromptText.replace(/\[name\]/g, memberNameText);
    //console.log(replacedText); // Outputs: "Hello, John! How are you today?"

    if (chatGPTpromptText) {
      this.conversationHistory.push({
        role: 'system',
        content: replacedText,
      });
    }
    //window.addEventListener('resize', () => this.handleResize());
  }

  public async init(): Promise<void> {
    const chatBox = this.elementsManager.getElement('chatBoxMessages');

    if (chatBox) {
      chatBox.scrollTop = chatBox.scrollHeight;
    }

    const sendButton = this.elementsManager.getElement('sendButton');
    if (sendButton) {
      const input = this.elementsManager.getInputElement('userInput');
      sendButton.addEventListener('click', () => this.sendMessage());
      input?.focus();

      input?.addEventListener('keydown', (event: KeyboardEvent) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.sendMessage();
        }
      });
    }
    this.sendMessage();
  }
  private handleInputFocus(): void {
    const chatBox = this.elementsManager.getElement('chatBox');
    chatBox?.classList.add('keyboard-visible');
    this.appendMessage('font-style-user', 'keyboard-visible');
  }

  private handleInputBlur(): void {
    const chatBox = this.elementsManager.getElement('chatBox');
    chatBox?.classList.remove('keyboard-visible');
    this.appendMessage('font-style-user', 'keyboard-not visible');
  }
  private appendMessage(who: string, text: string): void {
    const messageBox = document.createElement('div');
    messageBox.className = who;
    messageBox.textContent = text;
    gsap.from(messageBox, { autoAlpha: 0, duration: 1 });

    const chatBox = this.elementsManager.getElement('chatBoxMessages');
    // If the chatBox has more than 3 messages, fade the 3rd to last one.
    const messages = Array.from(chatBox?.children || []) as HTMLElement[];
    if (messages.length > 2) {
      const thirdToLastMessage = messages[messages.length - 2];
      gsap.to(thirdToLastMessage, { autoAlpha: 0.5, duration: 1 });
    }

    chatBox?.appendChild(messageBox);
    // Using SplitText to split the message into words.
    const split = new SplitText(messageBox, { type: 'words,chars' });
    gsap.set(split.words, { autoAlpha: 0 }); // Initially set all words to be transparent.

    // Animate each word sequentially.
    gsap.to(split.words, {
      autoAlpha: 1,
      duration: 0.5,
      stagger: 0.1,
      onComplete: () => {
        split.revert();
      },
    });
    // Smoothly scroll the chat box to the bottom using GSAP
    if (chatBox) {
      // Calculate total animation duration based on stagger and the number of words.
      const totalAnimationDuration = split.words.length * 0.1 + 0.5;
      this.animateScrollToBottom(chatBox, totalAnimationDuration);
    }
  }

  public async sendMessage(): Promise<void> {
    const input = this.elementsManager.getInputElement('userInput');
    let userMessage = '';

    if (input) {
      userMessage = input.value;
      input.value = ''; // Clear the input after capturing the message
    }

    if (userMessage.trim()) {
      this.appendMessage('font-style-user', userMessage);
      this.conversationHistory.push({ role: 'user', content: userMessage }); // Append user message to history
    }

    try {
      const requestBody = {
        model: 'gpt-4',
        messages: this.conversationHistory, // Use the conversation history for the API request
      };

      const response = await fetch('https://scripts.koalition.com/clone-me/proxy.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const responseData = await response.json();
      const botReply = responseData?.choices?.[0]?.message?.content as string;
      //console.log(responseData);
      //console.log(botReply);
      this.conversationHistory.push({ role: 'user', content: botReply }); // Append bot reply to history

      this.appendMessage('font-style-bot', botReply);
    } catch (error) {
      console.error('Error calling API via PHP proxy:', error);
      this.appendMessage('font-style-bot', 'Sorry, there was an issue processing your request.');
    }
  }
  private animateScrollToBottom(element: Element, duration = 0.5): void {
    const maxScroll = element.scrollHeight - element.clientHeight;

    gsap.to(element, {
      duration: duration,
      scrollTop: maxScroll,
      ease: 'power2.out',
    });
  }
  // private handleResize(): void {
  //   const chatBox = this.elementsManager.getElement('chatBox');
  //   if (chatBox) {
  //     const isKeyboardVisible = window.innerHeight < window.innerWidth;
  //     if (isKeyboardVisible) {
  //       chatBox.classList.add('keyboard-visible');
  //       alert('keyboard visible');
  //     } else {
  //       chatBox.classList.remove('keyboard-visible');
  //     }
  //   }
  // }
  private isMobileDevice(): boolean {
    // A simple regex test against the user agent string for common mobile device identifiers
    return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  }
}
